import React, { useEffect, useMemo, useState } from 'react';
import { Autocomplete, Box, Button, Grid2, Tab, Tabs, TextField, Typography } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';
import Navbar from '../setup/Navbar';
import StickyHeadTable from '../../BuiltInComponent/TableWithPagination';
import Loading from '../../BuiltInComponent/Loading';
import { getPOMasterData, getPOData } from '../../features/StockManagement/Purchases/poSlice';
import { getPDMasterData, getPDData } from '../../features/StockManagement/Purchases/pdSlice';
import { getPIMasterData, getPIData } from '../../features/StockManagement/Purchases/piSlice';
import { getIssuances, getProductionItems } from '../../features/AdminManagement/productionSlice';
import { getSupplierList } from '../../features/setup/useableSlice';
import { exportPurchaseReportPDF, exportPurchaseReportExcel, printPurchaseReport } from '../../utils/purchaseReportExport';
import '../../services/GlobalVariables';

const TAB_PO = 0;
const TAB_GRN = 1;
const TAB_PI = 2;
const TAB_MANUAL = 3;
const TAB_WASTAGE = 4;
const TAB_DISCARD = 5;
const SELECT_ALL_DEPARTMENTS = { departmentID: '__all__', departmentName: 'Select All Departments' };

const poColumns = [
    { fieldName: 'poID', cName: 'PO No', textAlign: 'left', width: 80, status: 'en', dType: 'string' },
    { fieldName: 'entDate', cName: 'Post Date', textAlign: 'left', width: 100, status: 'en', dType: 'date' },
    { fieldName: 'dispName', cName: 'Created By', textAlign: 'left', width: 120, status: 'en', dType: 'string' },
    { fieldName: 'PartyName', cName: 'Supplier', textAlign: 'left', width: 160, status: 'en', dType: 'string' },
    { fieldName: 'remarks', cName: 'Remarks', textAlign: 'left', width: 180, status: 'en', dType: 'string' },
    { fieldName: 'deliveryStatus', cName: 'Status', textAlign: 'left', width: 110, status: 'en', dType: 'string' },
    { fieldName: 'totalItems', cName: 'Total Items', textAlign: 'right', width: 80, status: 'en', dType: 'number' },
    // Dummy hidden action column: StickyHeadTable treats the last 3 columns as
    // action buttons unless a column with action: true exists.
    { fieldName: '__noop', cName: '', textAlign: 'center', width: 0, status: 'dis', dType: 'string', action: true },
];

const grnColumns = [
    { fieldName: 'pdID', cName: 'GRN No', textAlign: 'left', width: 80, status: 'en', dType: 'string' },
    { fieldName: 'entDate', cName: 'Post Date', textAlign: 'left', width: 100, status: 'en', dType: 'date' },
    { fieldName: 'dispName', cName: 'Created By', textAlign: 'left', width: 120, status: 'en', dType: 'string' },
    { fieldName: 'PartyName', cName: 'Supplier', textAlign: 'left', width: 160, status: 'en', dType: 'string' },
    { fieldName: 'grnSourceLabel', cName: 'GRN Source', textAlign: 'left', width: 110, status: 'en', dType: 'string' },
    { fieldName: 'remarks', cName: 'Remarks', textAlign: 'left', width: 160, status: 'en', dType: 'string' },
    { fieldName: 'pdStatus', cName: 'Status', textAlign: 'left', width: 90, status: 'en', dType: 'string' },
    { fieldName: 'totalItems', cName: 'Total Items', textAlign: 'right', width: 80, status: 'en', dType: 'number' },
    { fieldName: '__noop', cName: '', textAlign: 'center', width: 0, status: 'dis', dType: 'string', action: true },
];

const piColumns = [
    { fieldName: 'piID', cName: 'Invoice No', textAlign: 'left', width: 90, status: 'en', dType: 'string' },
    { fieldName: 'entDate', cName: 'Post Date', textAlign: 'left', width: 100, status: 'en', dType: 'date' },
    { fieldName: 'dispName', cName: 'Created By', textAlign: 'left', width: 120, status: 'en', dType: 'string' },
    { fieldName: 'PartyName', cName: 'Supplier', textAlign: 'left', width: 160, status: 'en', dType: 'string' },
    { fieldName: 'remarks', cName: 'Remarks', textAlign: 'left', width: 180, status: 'en', dType: 'string' },
    { fieldName: 'piStatus', cName: 'Status', textAlign: 'left', width: 90, status: 'en', dType: 'string' },
    { fieldName: 'totalItems', cName: 'Items', textAlign: 'right', width: 70, status: 'en', dType: 'number' },
    { fieldName: '__noop', cName: '', textAlign: 'center', width: 0, status: 'dis', dType: 'string', action: true },
];

const issuanceColumns = [
    { fieldName: 'issuanceID', cName: 'ID', textAlign: 'left', width: 70, status: 'en', dType: 'string' },
    { fieldName: 'recordedAt', cName: 'Date', textAlign: 'left', width: 100, status: 'en', dType: 'date' },
    { fieldName: 'issuedBy', cName: 'Issued By', textAlign: 'left', width: 110, status: 'en', dType: 'string' },
    { fieldName: 'branchName', cName: 'Branch', textAlign: 'left', width: 110, status: 'en', dType: 'string' },
    { fieldName: 'departmentName', cName: 'Department', textAlign: 'left', width: 110, status: 'en', dType: 'string' },
    { fieldName: 'totalItems', cName: 'Total Items', textAlign: 'right', width: 90, status: 'en', dType: 'number' },
    { fieldName: 'remarks', cName: 'Remarks', textAlign: 'left', width: 150, status: 'en', dType: 'string' },
    { fieldName: '__noop', cName: '', textAlign: 'center', width: 0, status: 'dis', dType: 'string', action: true },
];

const inDateRange = (dateValue, fromDate, toDate) => {
    if (!fromDate && !toDate) return true;
    const day = dayjs(dateValue);
    if (!day.isValid()) return true;
    if (fromDate && day.isBefore(dayjs(fromDate), 'day')) return false;
    if (toDate && day.isAfter(dayjs(toDate), 'day')) return false;
    return true;
};

// Tab key mapping for export column definitions
const TAB_KEY_MAP = {
    [TAB_PO]: 'po',
    [TAB_GRN]: 'grn',
    [TAB_PI]: 'pi',
    [TAB_MANUAL]: 'issuance',
    [TAB_WASTAGE]: 'issuance',
    [TAB_DISCARD]: 'issuance',
};

const buildDepartmentTotals = (groups = []) => {
    const byDepartment = new Map();
    groups.forEach((group) => {
        const departmentName = group.master?.departmentName || 'Unassigned Department';
        const current = byDepartment.get(departmentName) || { departmentName, documents: 0, quantity: 0, amount: 0 };
        current.documents += 1;
        (group.details || []).forEach((detail) => {
            current.quantity += Number(detail.quantity || 0);
            current.amount += Number(detail.amount || 0);
        });
        byDepartment.set(departmentName, current);
    });
    const departmentTotals = Array.from(byDepartment.values()).sort((a, b) => a.departmentName.localeCompare(b.departmentName));
    const grandTotals = departmentTotals.reduce((total, department) => ({
        documents: total.documents + department.documents,
        quantity: total.quantity + department.quantity,
        amount: total.amount + department.amount,
    }), { documents: 0, quantity: 0, amount: 0 });
    return { departmentTotals, grandTotals };
};
const TAB_TITLE_MAP = {
    [TAB_PO]: 'Purchase Order Summary',
    [TAB_GRN]: 'Purchase Delivery (GRN) Summary',
    [TAB_PI]: 'Purchase Invoice Summary',
    [TAB_MANUAL]: 'Manual Issuance Summary',
    [TAB_WASTAGE]: 'Wastage Summary',
    [TAB_DISCARD]: 'Discard Summary',
};

export default function PurchaseReport() {
    const dispatch = useDispatch();
    const [activeTab, setActiveTab] = useState(TAB_PO);
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const [selectedItem, setSelectedItem] = useState(null);
    const [selectedSupplier, setSelectedSupplier] = useState(null);
    const [selectedDepartments, setSelectedDepartments] = useState([]);
    // { [tab]: Set(docIDs) } — docs containing the selected item (null = not loaded yet)
    const [itemMatches, setItemMatches] = useState({});
    const [itemFilterLoading, setItemFilterLoading] = useState(false);
    const [exportLoading, setExportLoading] = useState(false);

    const { masterData: poData, isLoading: poLoading } = useSelector((state) => state.po);
    const { masterData: grnData, isLoading: grnLoading } = useSelector((state) => state.pd);
    const { masterData: piData, isLoading: piLoading } = useSelector((state) => state.pi);
    const { issuances, items, isLoading: issuanceLoading } = useSelector((state) => state.production);
    const { suppliers } = useSelector((state) => state.ru);
    const { user } = useSelector((state) => state.auth);

    const isIssuanceTab = activeTab === TAB_MANUAL || activeTab === TAB_WASTAGE || activeTab === TAB_DISCARD;
    const departmentOptions = useMemo(() => Array.from(
        new Map((issuances || [])
            .filter((row) => row.departmentID)
            .map((row) => [row.departmentID, { departmentID: row.departmentID, departmentName: row.departmentName || 'Unnamed Department' }])
        ).values()
    ).sort((a, b) => a.departmentName.localeCompare(b.departmentName)), [issuances]);

    useEffect(() => {
        dispatch(getProductionItems());
        dispatch(getSupplierList());
    }, [dispatch]);

    useEffect(() => {
        if (activeTab === TAB_PO) dispatch(getPOMasterData());
        if (activeTab === TAB_GRN) dispatch(getPDMasterData());
        if (activeTab === TAB_PI) dispatch(getPIMasterData());
        if (activeTab === TAB_MANUAL) dispatch(getIssuances({ issuanceType: 'manual' }));
        if (activeTab === TAB_WASTAGE) dispatch(getIssuances({ issuanceType: 'wastage' }));
        if (activeTab === TAB_DISCARD) dispatch(getIssuances({ issuanceType: 'discard' }));
    }, [activeTab, dispatch]);

    // Item tab change par reset karein
    useEffect(() => {
        setSelectedItem(null);
        setSelectedSupplier(null);
        setSelectedDepartments([]);
        setFromDate('');
        setToDate('');
    }, [activeTab]);

    // Item filter: PO/GRN/PI master lists item rows nahi rakhte, is liye
    // sirf item select hone par har document ka detail fetch kar ke match karte hain.
    useEffect(() => {
        if (isIssuanceTab) return; // issuance tabs server-side item filter support karte hain
        if (!selectedItem) return;
        if (itemMatches[activeTab]) return; // already loaded for this tab

        const docs = activeTab === TAB_PO ? (poData || []) : activeTab === TAB_GRN ? (grnData || []) : (piData || []);
        if (!docs.length) return;

        const idField = activeTab === TAB_PO ? 'poID' : activeTab === TAB_GRN ? 'pdID' : 'piID';
        const detailKey = activeTab === TAB_PI ? 'pi' : 'pd';
        const getDetail = activeTab === TAB_PO ? getPOData : activeTab === TAB_GRN ? getPDData : getPIData;
        const itemCode = Number(selectedItem.itemCode);
        let cancelled = false;

        setItemFilterLoading(true);
        Promise.all(docs.map((doc) =>
            dispatch(getDetail(doc[idField])).unwrap()
                .then((data) => {
                    const detailRows = data?.[detailKey] || [];
                    return detailRows.some((d) => Number(d.itemCode) === itemCode) ? doc[idField] : null;
                })
                .catch(() => null)
        )).then((results) => {
            if (cancelled) return;
            setItemMatches((prev) => ({ ...prev, [activeTab]: new Set(results.filter((id) => id !== null)) }));
            setItemFilterLoading(false);
        });

        return () => { cancelled = true; };
    }, [selectedItem, activeTab, isIssuanceTab, poData, grnData, piData, itemMatches, dispatch]);

    // Issuance tabs: item filter server-side
    useEffect(() => {
        if (!isIssuanceTab || !selectedItem) return;
        const type = activeTab === TAB_MANUAL ? 'manual' : activeTab === TAB_WASTAGE ? 'wastage' : 'discard';
        dispatch(getIssuances({ issuanceType: type, itemCode: selectedItem.itemCode }));
    }, [selectedItem, activeTab, isIssuanceTab, dispatch]);

    const { columns, rows, isLoading } = useMemo(() => {
        let base;
        if (activeTab === TAB_PO) base = { columns: poColumns, data: poData || [], loading: poLoading, idField: 'poID' };
        else if (activeTab === TAB_GRN) base = { columns: grnColumns, data: grnData || [], loading: grnLoading, idField: 'pdID' };
        else if (activeTab === TAB_PI) base = { columns: piColumns, data: piData || [], loading: piLoading, idField: 'piID' };
        else base = { columns: issuanceColumns, data: issuances || [], loading: issuanceLoading, idField: 'issuanceID' };

        let filtered = base.data.filter((row) => inDateRange(row.entDate || row.recordedAt || row.issuedAt, fromDate, toDate));

        // Client-side item filter for PO/GRN/PI
        if (!isIssuanceTab && selectedItem && itemMatches[activeTab]) {
            const matchSet = itemMatches[activeTab];
            filtered = filtered.filter((row) => matchSet.has(row[base.idField]));
        }

        if (isIssuanceTab && selectedDepartments.length) {
            const selectedDepartmentIDs = new Set(selectedDepartments.map((department) => Number(department.departmentID)));
            filtered = filtered.filter((row) => selectedDepartmentIDs.has(Number(row.departmentID)));
        }

        // Vendor/Supplier filter for PO/GRN/PI
        if (!isIssuanceTab && selectedSupplier) {
            filtered = filtered.filter((row) => String(row.PartyName || '').trim() === String(selectedSupplier.PartyName || selectedSupplier.partyName || '').trim());
        }

        if (isIssuanceTab) {
            const grouped = new Map();
            filtered.forEach((detail) => {
                const id = detail.issuanceID || detail.referenceID;
                if (!grouped.has(id)) grouped.set(id, { ...detail, issuanceID: id, details: [] });
                grouped.get(id).details.push(detail);
            });
            filtered = Array.from(grouped.values()).map((issuance) => ({ ...issuance, totalItems: issuance.details.length }));
        }

        return { columns: base.columns, rows: filtered, isLoading: base.loading || itemFilterLoading };
    }, [activeTab, poData, grnData, piData, issuances, poLoading, grnLoading, piLoading, issuanceLoading, fromDate, toDate, selectedItem, selectedSupplier, selectedDepartments, itemMatches, isIssuanceTab, itemFilterLoading]);

    // Mandatory filter check — report export ke liye kam az kam ek filter lazmi hai
    const hasMandatoryFilter = Boolean(fromDate || toDate || selectedItem || selectedSupplier || selectedDepartments.length);

    // Item code -> tradePrice map (itemsinformation se fallback rate ke liye)
    const itemRateMap = useMemo(() => {
        const map = {};
        (items || []).forEach((it) => { map[it.itemCode] = Number(it.tradePrice || 0); });
        return map;
    }, [items]);

    // Fetch detail rows for every visible master and build grouped report data
    const buildExportGroups = async () => {
        if (isIssuanceTab) {
            // Issuance rows are already detail-level; group by issuanceID
            const grouped = new Map();
            rows.forEach((row) => {
                const id = row.issuanceID;
                if (!grouped.has(id)) grouped.set(id, { master: row, details: [] });
                (row.details || []).forEach((detail) => {
                    const rate = itemRateMap[detail.itemCode] || 0;
                    grouped.get(id).details.push({ ...detail, rate, amount: rate * Number(detail.quantity || 0) });
                });
            });
            return Array.from(grouped.values());
        }

        const idField = activeTab === TAB_PO ? 'poID' : activeTab === TAB_GRN ? 'pdID' : 'piID';
        const detailKey = activeTab === TAB_PI ? 'pi' : 'pd';
        const getDetail = activeTab === TAB_PO ? getPOData : activeTab === TAB_GRN ? getPDData : getPIData;

        const groups = await Promise.all(rows.map(async (master) => {
            try {
                const data = await dispatch(getDetail(master[idField])).unwrap();
                // Backend ab rate fallback deta hai (detail.itemRate > 0 else itemsinformation.tradePrice)
                const details = (data?.[detailKey] || []).map((d) => {
                    const rate = Number(d.itemRate || 0) > 0 ? Number(d.itemRate) : (itemRateMap[d.itemCode] || 0);
                    return { ...d, itemRate: rate, amount: rate * Number(d.quantity || 0) };
                });
                return { master, details };
            } catch {
                return { master, details: [] };
            }
        }));
        return groups;
    };

    const getExportFileName = () => {
        const parts = [TAB_TITLE_MAP[activeTab]];
        if (fromDate) parts.push(`From-${fromDate}`);
        if (toDate) parts.push(`To-${toDate}`);
        if (selectedItem) parts.push(`Item-${selectedItem.itemCode || selectedItem.itemDesc || ''}`);
        if (selectedSupplier) parts.push(`Supplier-${selectedSupplier.PartyName || selectedSupplier.partyName || ''}`);
        if (selectedDepartments.length) parts.push(`Department-${selectedDepartments.map((department) => department.departmentName).filter(Boolean).join('-')}`);
        return parts.join('_').replace(/[^a-zA-Z0-9_-]+/g, '_').replace(/_+/g, '_').replace(/^_|_$/g, '').slice(0, 150);
    };
    const getExportPayload = (groups) => ({
        title: TAB_TITLE_MAP[activeTab],
        fileName: getExportFileName(),
        tabKey: TAB_KEY_MAP[activeTab],
        groups,
        filters: {
            fromDate,
            toDate,
            itemLabel: selectedItem ? `${selectedItem.itemDesc || ''} (${selectedItem.itemCode})` : '',
            supplierLabel: selectedSupplier ? (selectedSupplier.PartyName || selectedSupplier.partyName || '') : '',
            departmentLabel: selectedDepartments.map((department) => department.departmentName).join(', '),
            departmentTotals: isIssuanceTab ? buildDepartmentTotals(groups).departmentTotals : [],
            grandTotals: isIssuanceTab ? buildDepartmentTotals(groups).grandTotals : null,
        },
        user,
    });

    const runExport = async (exportFn) => {
        if (!hasMandatoryFilter) return alert('Report export/print karne se pehle kam az kam ek filter lazmi hai (From Date, To Date, Item, Supplier, ya Department).');
        if (!rows.length) return alert('Is filter mein koi record nahi mila.');
        if (rows.length > 200) return alert(`Bohot zyada records hain (${rows.length} masters). Filters aur tight karein (date range chhoti karein ya item/supplier select karein).`);
        setExportLoading(true);
        try {
            const groups = await buildExportGroups();
            exportFn(getExportPayload(groups));
        } catch (error) {
            alert('Export failed: ' + (error?.message || error));
        } finally {
            setExportLoading(false);
        }
    };

    const onPrint = () => runExport(printPurchaseReport);
    const onExportPDF = () => runExport(exportPurchaseReportPDF);
    const onExportExcel = () => runExport(exportPurchaseReportExcel);

    return (
        <>
            <Navbar />
            <Box sx={{ p: 2 }}>
                <Typography variant="h5" sx={{ fontWeight: 600 }}>
                    Purchase Report
                </Typography>

                <Box sx={{ borderBottom: 1, borderColor: 'divider', mt: 2 }}>
                    <Tabs value={activeTab} onChange={(_, value) => setActiveTab(value)}>
                        <Tab label="Purchase Order" />
                        <Tab label="Purchase Delivery (GRN)" />
                        <Tab label="Purchase Invoice" />
                        <Tab label="Manual Issuance" />
                        <Tab label="Wastage" />
                        <Tab label="Discard" />
                    </Tabs>
                </Box>

                {/* Filters */}
                <Grid2 container spacing={2} sx={{ mt: 1, mb: 1 }} alignItems="center">
                    <Grid2 size={{ xs: 12, sm: 3, md: 2 }}>
                        <TextField
                            fullWidth
                            size="small"
                            type="date"
                            label="From Date"
                            InputLabelProps={{ shrink: true }}
                            value={fromDate}
                            onChange={(e) => setFromDate(e.target.value)}
                        />
                    </Grid2>
                    <Grid2 size={{ xs: 12, sm: 3, md: 2 }}>
                        <TextField
                            fullWidth
                            size="small"
                            type="date"
                            label="To Date"
                            InputLabelProps={{ shrink: true }}
                            value={toDate}
                            onChange={(e) => setToDate(e.target.value)}
                        />
                    </Grid2>
                    <Grid2 size={{ xs: 12, sm: 4, md: 3 }}>
                        <Autocomplete
                            size="small"
                            options={items || []}
                            getOptionLabel={(o) => (o.itemDesc ? `${o.itemDesc} (${o.itemCode})` : String(o.itemCode || ''))}
                            value={selectedItem}
                            onChange={(_, val) => setSelectedItem(val)}
                            isOptionEqualToValue={(o, v) => o.itemCode === v.itemCode}
                            renderInput={(params) => <TextField {...params} label="Item (Optional)" />}
                        />
                    </Grid2>
                    {!isIssuanceTab && (
                        <Grid2 size={{ xs: 12, sm: 4, md: 3 }}>
                            <Autocomplete
                                size="small"
                                options={suppliers || []}
                                getOptionLabel={(o) => o.PartyName || o.partyName || ''}
                                value={selectedSupplier}
                                onChange={(_, val) => setSelectedSupplier(val)}
                                isOptionEqualToValue={(o, v) => (o.PartyCode || o.partyCode) === (v.PartyCode || v.partyCode)}
                                renderInput={(params) => <TextField {...params} label="Supplier / Vendor (Optional)" />}
                            />
                        </Grid2>
                    )}
                    {isIssuanceTab && (
                        <Grid2 size={{ xs: 12, sm: 5, md: 4 }}>
                            <Autocomplete
                                multiple
                                size="small"
                                options={[SELECT_ALL_DEPARTMENTS, ...departmentOptions]}
                                value={selectedDepartments}
                                getOptionLabel={(department) => department.departmentName || ''}
                                isOptionEqualToValue={(option, value) => String(option.departmentID) === String(value.departmentID)}
                                onChange={(_, values) => {
                                    if ((values || []).some((department) => department.departmentID === SELECT_ALL_DEPARTMENTS.departmentID)) {
                                        setSelectedDepartments(departmentOptions);
                                    } else {
                                        setSelectedDepartments(values || []);
                                    }
                                }}
                                renderInput={(params) => <TextField {...params} label="Departments (Optional)" placeholder="Select departments" />}
                            />
                        </Grid2>
                    )}
                    <Grid2 size={{ xs: 12, sm: 2, md: 1 }}>
                        <Button
                            variant="outlined"
                            size="medium"
                            onClick={() => { setFromDate(''); setToDate(''); setSelectedItem(null); setSelectedSupplier(null); setSelectedDepartments([]); }}
                        >
                            Clear
                        </Button>
                    </Grid2>
                    {itemFilterLoading && (
                        <Grid2 size={12}>
                            <Typography variant="caption" color="text.secondary">
                                Item filter ke liye documents check ho rahe hain...
                            </Typography>
                        </Grid2>
                    )}
                    {/* Export buttons */}
                    <Grid2 size={12} sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                        <Button variant="contained" size="small" onClick={onPrint} disabled={exportLoading}>Print</Button>
                        <Button variant="contained" size="small" color="secondary" onClick={onExportPDF} disabled={exportLoading}>Export PDF</Button>
                        <Button variant="contained" size="small" color="success" onClick={onExportExcel} disabled={exportLoading}>Export Excel</Button>
                        {exportLoading && (
                            <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
                                Details load ho rahi hain...
                            </Typography>
                        )}
                        {!hasMandatoryFilter && (
                            <Typography variant="caption" color="warning.main" sx={{ alignSelf: 'center' }}>
                                Export/Print ke liye kam az kam ek filter zaroori hai
                            </Typography>
                        )}
                    </Grid2>
                </Grid2>

                <Box sx={{ mt: 2 }}>
                    {isLoading ? (
                        <Loading />
                    ) : (
                        <StickyHeadTable tableColumns={columns} tableData={rows} />
                    )}
                </Box>
            </Box>
        </>
    );
}
